import type { RecordingIntakeAgentMappingEntry } from "@repo/app-types/entities/recordingIntake";
import { Input } from "@repo/ui/Inputs/input";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { Size } from "@repo/ui/helpers/helpers";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { useMemo, useState, type FC } from "react";
import { useRecordingS3IntakeAgentEmailMapQuery } from "../../../api/entities/recordingS3IntakeIntegration.js";

// EntityTable rows need a stable `id`; the agent username is unique per map.
interface AgentMappingRow extends RecordingIntakeAgentMappingEntry {
  id: string;
}

const columns: readonly EntityTableColumnDefinition<AgentMappingRow>[] = [
  {
    id: "username",
    header: "Agent username",
    isRowHeader: true,
    getTextValue: (row) => row.username,
    renderCell: (row) => (
      <span className="font-mono text-xs break-all">{row.username}</span>
    ),
  },
  {
    id: "email",
    header: "Teammate email",
    getTextValue: (row) => row.email,
    renderCell: (row) => <span className="break-all">{row.email}</span>,
  },
];

/**
 * The read-only RingCX agent username → teammate email mapping, shown in the
 * Settings tab for RingCX intake customers only. The customer twin of the HQ
 * tooling section, minus the live "Refresh cache" action, the teammate deep
 * links, and every cache-internal detail (TTL, Valkey specifics): customers see
 * the pairs and a filter, nothing more. A cold cache and an unreachable backend
 * each get their own plain-language empty state.
 */
export const RecordingS3IntakeAgentEmailMapping: FC = () => {
  const { data, isLoading } = useRecordingS3IntakeAgentEmailMapQuery();
  const [filter, setFilter] = useState("");

  const entries = useMemo(() => data?.entries ?? [], [data?.entries]);
  const filtered = useMemo<AgentMappingRow[]>(() => {
    const needle = filter.trim().toLowerCase();
    return entries
      .filter(
        (entry) =>
          !needle ||
          entry.username.toLowerCase().includes(needle) ||
          entry.email.toLowerCase().includes(needle),
      )
      .map((entry) => ({ id: entry.username, ...entry }));
  }, [entries, filter]);

  return (
    <div className="space-y-4 border-t border-border-light pt-6">
      <div>
        <p className="text-sm font-medium text-foreground">
          Agent email mapping
        </p>
        <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
          How each call-center agent username maps to an Alpharun teammate. Used
          to attribute imported recordings to the right teammate.
        </p>
      </div>

      {isLoading && !data ? (
        <div className="flex py-4">
          <Loader style={LoaderStyle.SPINNER} />
        </div>
      ) : data && !data.available ? (
        <p className="text-sm text-muted-foreground">
          The mapping is temporarily unavailable. Please try again shortly.
        </p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No agent mapping yet — it appears here once recordings begin
          importing.
        </p>
      ) : (
        <div className="space-y-3">
          {/* Count the rows actually shown, so it stays consistent with the
              table once a filter is applied. */}
          <span className="text-sm text-muted-foreground">
            {filtered.length} agent{filtered.length === 1 ? "" : "s"}
          </span>
          <Input
            label="Filter"
            value={filter}
            placeholder="Filter by username or email"
            onChange={(event) => setFilter(event.target.value)}
          />
          <div className="max-h-96 overflow-auto rounded-lg border border-border-light">
            <EntityTable
              aria-label="Agent email mapping"
              columns={columns}
              entities={filtered}
              size={Size.SMALL}
              emptyMessage={`No agents match “${filter}”.`}
            />
          </div>
        </div>
      )}
    </div>
  );
};
